import { accessValue } from '../../../core/utils/accessors/access-value.js';
import type { ChartData } from '../../core/types/chart-data.js';
import type { ChartUnit } from '../../core/types/chart-unit.js';
import type { PieChartData, SliceData } from '../types/pie-chart.config.js';

/**
 * Input accepted by {@link normalizePieData}: either the unified `ChartData`
 * format (paired with `labelAccessor`/`valueAccessor` and a root-level `unit`)
 * or the legacy `PieChartData` shape.
 * @internal
 */
export interface NormalizePieDataInput {
  // oxlint-disable-next-line typescript/no-deprecated
  data: ChartData | PieChartData;
  labelAccessor?: string;
  valueAccessor?: string;
  unit?: ChartUnit;
}

/**
 * Normalizes the public PieChart/DonutChart data props into the internal
 * `PieChartData` (`{ slices, unit }`) shape consumed by the rendering pipeline.
 *
 * When `labelAccessor`/`valueAccessor` are provided, the data is treated as the
 * unified `ChartData` array and each datapoint is mapped to a slice by resolving
 * the accessor strings (dot-notation supported). Otherwise the legacy
 * `PieChartData` is returned unchanged.
 *
 * @param input - the data-related PieChart/DonutChart props
 * @returns the internal `PieChartData` shape
 * @internal
 */
// oxlint-disable-next-line typescript/no-deprecated
export function normalizePieData(input: NormalizePieDataInput): PieChartData {
  const { data, labelAccessor, valueAccessor, unit } = input;

  if (labelAccessor && valueAccessor) {
    // oxlint-disable-next-line typescript/no-deprecated
    const slices: SliceData[] = (data as ChartData).map((datapoint) => {
      const color = accessValue<string>(datapoint, 'color');
      return {
        category: String(accessValue<string>(datapoint, labelAccessor) ?? ''),
        value: Number(accessValue<number>(datapoint, valueAccessor) ?? 0),
        ...(typeof color === 'string' ? { color } : {}),
      };
    });

    return { slices, unit };
  }

  // oxlint-disable-next-line typescript/no-deprecated
  return data as PieChartData;
}
